import { useMutation, useQueryClient } from 'react-query'
import { message } from 'antd'
import { postJSON, getJSON } from 'common/services/api'

type UseReferralAttachmentRemoveProps = {
  residentId: string
  onSuccess?: () => void
}

type UseReferralAttachmentRemoveReturn = {
  removeAttachment: (referralId: string, attachmentId: string) => Promise<void>
  isRemoving: boolean
}

export const useReferralAttachmentRemove = ({
  residentId,
  onSuccess
}: UseReferralAttachmentRemoveProps): UseReferralAttachmentRemoveReturn => {
  const queryClient = useQueryClient()

  const removeAttachmentFunction = async ({
    referralId,
    attachmentId
  }: {
    referralId: string
    attachmentId: string
  }) => {
    // Fetch current referral to get existing attachments
    const response = await getJSON({
      url: `/api/list/referrals/${referralId}`
    })

    const referral = response.result

    if (!referral || !referral.attachments) {
      throw new Error('Referral not found or has no attachments')
    }

    // Filter out the attachment to remove and extract IDs
    const updatedAttachments = referral.attachments
      .filter((att: any) => att._id !== attachmentId && att.id !== attachmentId)
      .map((att: any) => att._id || att.id)

    // Update with new attachments array
    return postJSON({
      url: `/api/list/referrals/${referralId}`,
      method: 'PUT',
      json: {
        attachments: updatedAttachments
      }
    })
  }

  const { mutateAsync: removeMutation, isLoading: isRemoving } = useMutation(
    removeAttachmentFunction,
    {
      onSuccess: () => {
        message.success('Attachment removed successfully')
        queryClient.invalidateQueries(['referrals', residentId])
        if (onSuccess) {
          onSuccess()
        }
      },
      onError: (error: any) => {
        message.error(error?.message || 'Failed to remove attachment')
      }
    }
  )

  const removeAttachment = async (referralId: string, attachmentId: string) => {
    await removeMutation({ referralId, attachmentId })
  }

  return {
    removeAttachment,
    isRemoving
  }
}
